var _a;
import { validateProducts, validateFilters, validateSearchMeta, validateSortOptions } from '../utils/validation';
import { pickVideoSource } from '../../utils/pickVideoSource';
// Boost does not provide data in "media" object by default. This is a custom feature that we need to ask Boost to enable.
// Only "media" object contains videos from product media.
const USE_BOOST_PRODUCT_MEDIA_OBJECT = String((_a = process.env.USE_BOOST_PRODUCT_MEDIA_OBJECT) !== null && _a !== void 0 ? _a : '').toLowerCase() === 'true';
function mapMediaToImages(media) {
    var _a, _b, _c;
    if (!Array.isArray(media))
        return [];
    const result = [];
    for (const entry of media) {
        if ((entry === null || entry === void 0 ? void 0 : entry.status) && entry.status !== 'READY')
            continue;
        if ((entry === null || entry === void 0 ? void 0 : entry.mediaContentType) === 'IMAGE' && ((_a = entry.image) === null || _a === void 0 ? void 0 : _a.originalSrc)) {
            result.push({
                url: entry.image.originalSrc,
                alt: entry.alt || entry.image.altText || '',
            });
        }
        else if ((entry === null || entry === void 0 ? void 0 : entry.mediaContentType) === 'VIDEO') {
            const chosen = pickVideoSource(entry.sources);
            if (chosen === null || chosen === void 0 ? void 0 : chosen.url) {
                result.push({
                    url: chosen.url,
                    alt: entry.alt || '',
                    video_thumbnail: (_c = (_b = entry.preview) === null || _b === void 0 ? void 0 : _b.image) === null || _c === void 0 ? void 0 : _c.originalSrc,
                });
            }
        }
    }
    return result;
}
/** Type guard for the inline banner pseudo-items Boost interleaves into `products`. */
function isInlineBanner(item) {
    return item.is_banner === true;
}
/** Inverse guard — narrows the union to `BoostProduct` for the mapping path. */
function isBoostProduct(item) {
    return !isInlineBanner(item);
}
export function mapProducts(items) {
    const mappedProducts = items.filter(isBoostProduct).map((product, index) => {
        var _a, _b;
        const price = product.price_max | 0;
        const compareAtPrice = product.compare_at_price_max ? product.compare_at_price_max : 0;
        const data = {
            id: String(product.id),
            title: product.title,
            handle: product.handle,
            brand: product.vendor,
            color: '',
            size: [
                ''
            ],
            price: {
                regular: price >= compareAtPrice ? price : compareAtPrice,
                sale: price >= compareAtPrice ? undefined : price,
                special: undefined
            },
            image: {
                url: product.images_info && ((_a = product.images_info[0]) === null || _a === void 0 ? void 0 : _a.src) || '',
                alt: product.images_info && ((_b = product.images_info[0]) === null || _b === void 0 ? void 0 : _b.alt) || `${product.title}-image`,
            },
            images: USE_BOOST_PRODUCT_MEDIA_OBJECT
                ? mapMediaToImages(product.media)
                : (product.images_info && product.images_info.map((image) => image.src) || []),
            availability: product.available ? 'in_stock' : 'out_of_stock',
            productType: product.product_type,
            tags: product.tags || [],
            // Pass through raw variants for color swatch functionality (bypassing strict validation)
            customAttributes: {
                boostVariants: product.variants || [],
                boostRawProduct: product, // Store the raw product for quick shop modal
            },
            metafields: product.metafields || [],
        };
        return data;
    });
    return validateProducts(mappedProducts, 'Boost');
}
export function mapFilters(filter, currentFilters) {
    const filters = [];
    if (!filter || !filter.options) {
        console.warn('[Boost Mapper] No filter or filter.options provided');
        return filters;
    }
    filter.options.map(option => {
        const filter = {
            id: option.filterOptionId,
            label: option.label,
            type: 'select',
            options: []
        };
        if (option.displayType === 'list' && Array.isArray(option.values)) {
            filter.type = 'select';
            filter.options = option.values.map((value) => {
                var _a, _b;
                const rawLabel = String(value.displayName || value.label || value.key);
                // Boost tag filters use escaped prefixes (e.g. "occasion\\:"), strip backslashes before slicing
                const unescapedPrefix = (_a = option.prefix) === null || _a === void 0 ? void 0 : _a.replace(/\\/g, '');
                const label = option.filterType === 'tag' && unescapedPrefix
                    ? rawLabel.slice(unescapedPrefix.length)
                    : rawLabel;
                return {
                    id: String(value.key),
                    label,
                    count: Number(value.doc_count),
                    selected: ((_b = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters[option.filterOptionId]) === null || _b === void 0 ? void 0 : _b.includes(String(value.key))) || false,
                    value: String(value.key)
                };
            });
        }
        if (option.displayType === 'range') {
            filter.type = 'range';
            const rangeValues = option.values;
            if (option.filterType === 'price' && (currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.pf_p_price)) {
                filter.currentMin = Number(currentFilters.pf_p_price[0].split(":")[0]);
                filter.currentMax = Number(currentFilters.pf_p_price[0].split(":")[1]);
            }
            else {
                filter.currentMin = rangeValues === null || rangeValues === void 0 ? void 0 : rangeValues.min;
                filter.currentMax = rangeValues === null || rangeValues === void 0 ? void 0 : rangeValues.max;
            }
            filter.min = rangeValues === null || rangeValues === void 0 ? void 0 : rangeValues.min;
            filter.max = rangeValues === null || rangeValues === void 0 ? void 0 : rangeValues.max;
        }
        filters.push(filter);
    });
    return validateFilters(filters, 'Boost');
}
/**
 * Extract banner pseudo-items Boost interleaves into the `products` array (with
 * `is_banner: true`) and surface them in the shape `ProductGrid` already
 * consumes — i.e. `BoostBanner` where the `position` field is the page-relative
 * insertion index. Boost provides this directly as `relative_position`; the
 * top-level `position` field on the inline item is the global index across all
 * paginated results and is wrong for the grid past page 1.
 *
 * Recommendation responses never contain banners, so this just returns `[]` for
 * those callers.
 */
function extractInlineBanners(items) {
    if (!Array.isArray(items))
        return [];
    return items.filter(isInlineBanner).map(b => {
        var _a;
        return ({
            imgSrc: b.imgSrc,
            link: b.link,
            name: b.name,
            openInNewTab: b.openInNewTab,
            // Map `relative_position` (page-relative index — what `ProductGrid` wants)
            // onto the abstract `position` field. Fall back to `position` if Boost ever
            // omits `relative_position` on page 1, where the two values are equal.
            position: (_a = b.relative_position) !== null && _a !== void 0 ? _a : b.position,
        });
    });
}
export function mapMeta(data, page, pageSize) {
    var _a;
    // Boost's search/filter endpoint returns `total_product`, while the
    // recommendations endpoint (`/discovery/recommend`) returns `total`. Prefer
    // whichever is present; fall back to the length of the products array, then
    // to 0 so the Zod schema always sees a valid number. The products-array
    // fallback excludes inline banner pseudo-items so they don't inflate the count.
    const productsOnlyLength = Array.isArray(data.products)
        ? data.products.filter(isBoostProduct).length
        : 0;
    const totalResults = typeof data.total_product === 'number' ? data.total_product :
        typeof data.total === 'number' ? data.total :
            productsOnlyLength;
    const safePageSize = pageSize > 0 ? pageSize : 1;
    // Banners can arrive in two places depending on how Boost responds:
    //   1. Inline pseudo-items inside `data.products` (when `insert_banners=true`
    //      is sent on the request). These carry `relative_position` — the
    //      page-relative slot — which is what `ProductGrid` wants.
    //   2. A top-level `data.banners` array (older response shape).
    // Prefer inline when present (richer + correctly positioned per page); fall
    // back to top-level otherwise.
    const inlineBanners = extractInlineBanners(data.products);
    const banners = inlineBanners.length > 0 ? inlineBanners : data.banners;
    return validateSearchMeta({
        totalResults,
        totalPages: Math.ceil(totalResults / safePageSize),
        currentPage: page,
        pageSize: safePageSize,
        banners: banners,
        // Boost's per-response correlation token, surfaced as the provider-agnostic
        // `analytics.queryID`. It flows into the TrackingProvider's `correlationId`
        // and onto /e events as `rid` — per Boost's dev doc, the event rid must be
        // "obtained from our API response meta.rid".
        analytics: ((_a = data.meta) === null || _a === void 0 ? void 0 : _a.rid) ? { queryID: data.meta.rid } : undefined,
    }, 'Boost');
}
export function mapSorts() {
    return validateSortOptions([
        { id: 'manual', label: 'Recommended' },
        { id: 'price_asc', label: 'Price: Low to High' },
        { id: 'price_desc', label: 'Price: High to Low' },
        { id: 'title_asc', label: 'Name: A to Z' },
        { id: 'title_desc', label: 'Name: Z to A' },
    ], 'Boost');
}
